"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";
import { useTheme } from "../components/ThemeProvider";

/**
 * `<ThemeToggleButton>` — light/dark toggle wired to the existing
 * `ThemeProvider`. Part of the rail family (`aisquare-ui#128`).
 *
 * Cycles `light ⇄ dark` via `useTheme().setTheme`, persisted by the provider
 * (localStorage). Carries `aria-pressed={resolvedTheme === "dark"}` and an
 * accessible name so screen readers convey the state (Smashing Magazine
 * accessible-dark-mode pattern). The sun/moon glyph swaps with the resolved
 * theme — instantly under `prefers-reduced-motion`.
 *
 * Press: this is a ROUND/icon rail control, so it keeps its shape and shares
 * the Onyx offset-key language only via the press — a springy 1px press-DOWN
 * on `:active` (a CSS translate + overshoot bounce, the same ease every
 * Button/IconButton/KeycapButton uses), so the whole lib presses the same way.
 * The Onyx hex notch + travel block are for rectangular surfaces, NOT this.
 *
 * Variants:
 * - `rail` — icon-only tile sized to sit in the `<NavRail>` foot (matches the
 *   Help button), with a hover-reveal label ("Light"/"Dark") to the right,
 *   exactly like the nav buttons.
 * - `inline` — a labelled pill (icon + text), for settings panes / menus.
 *
 * Must be rendered inside a `<ThemeProvider>` (it calls `useTheme()`).
 *
 * @example
 *   <ThemeToggleButton variant="rail" />
 *   <ThemeToggleButton variant="inline" />
 */
export const themeToggleVariants = cva(
  [
    "group/themetoggle relative inline-flex items-center outline-none",
    // Hover color/tint transition + the shared Onyx offset-key press on one
    // transition-property (two `transition-*` utilities would clobber each
    // other's transition-property). The springy overshoot ease drives the 1px
    // press-DOWN release the same way every Button/IconButton/KeycapButton does.
    "transition-[color,background-color,border-color,transform] duration-150 ease-[cubic-bezier(0.34,1.45,0.6,1)]",
    "active:translate-y-px",
    "text-[var(--ink-2,hsl(var(--muted-foreground)))]",
    "focus-visible:ring-2 focus-visible:ring-[var(--forest,hsl(var(--ring)))] focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--surface,hsl(var(--background)))]",
    "disabled:pointer-events-none disabled:opacity-50",
  ].join(" "),
  {
    variants: {
      variant: {
        // Icon-only rail tile — matches the Help button (32×32), hover tints --ink.
        rail: cn(
          "justify-center rounded-[12px]",
          "hover:bg-[var(--surface-2,hsl(var(--muted)))] hover:text-[var(--ink,hsl(var(--foreground)))]",
        ),
        // Labelled pill — the "Switch rail" stage-toggle treatment.
        inline: cn(
          "gap-2 rounded-full px-3.5 py-[7px] text-[12.5px] font-medium",
          "border border-[var(--hairline,hsl(var(--border)))] bg-[var(--surface-2,hsl(var(--muted)))]",
          "hover:border-[var(--forest,hsl(var(--accent)))] hover:text-[var(--forest,hsl(var(--accent)))]",
        ),
      },
      size: {
        default: "[&_svg]:h-[18px] [&_svg]:w-[18px]",
        compact: "[&_svg]:h-4 [&_svg]:w-4",
      },
    },
    compoundVariants: [
      { variant: "rail", size: "default", class: "h-8 w-8" },
      { variant: "rail", size: "compact", class: "h-7 w-7" },
    ],
    defaultVariants: {
      variant: "rail",
      size: "default",
    },
  },
);

/** Sun glyph — shown when the resolved theme is dark (click → go light). */
function SunIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
}

/** Moon glyph — shown when the resolved theme is light (click → go dark). */
function MoonIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </svg>
  );
}

type ThemeToggleElementProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "children" | "onClick"
>;

export interface ThemeToggleButtonProps
  extends ThemeToggleElementProps, VariantProps<typeof themeToggleVariants> {
  /** Override the accessible name. Default `"Toggle theme"`. */
  "aria-label"?: string;
}

export const ThemeToggleButton = React.forwardRef<HTMLButtonElement, ThemeToggleButtonProps>(
  ({ variant, size, className, "aria-label": ariaLabel = "Toggle theme", ...rest }, ref) => {
    const { resolvedTheme, setTheme } = useTheme();
    const isDark = resolvedTheme === "dark";
    // When dark, the affordance is "go to light" → show the sun, label "Light".
    const targetLabel = isDark ? "Light" : "Dark";
    const isRail = (variant ?? "rail") === "rail";

    return (
      <button
        ref={ref}
        type="button"
        aria-pressed={isDark}
        aria-label={ariaLabel}
        onClick={() => setTheme(isDark ? "light" : "dark")}
        className={cn(themeToggleVariants({ variant, size }), className)}
        {...rest}
      >
        <span className="flex items-center justify-center [&_svg]:shrink-0">
          {isDark ? <SunIcon /> : <MoonIcon />}
        </span>

        {isRail ? (
          // Hover/focus-reveal label to the right — mirrors RailNavButton.
          <span
            role="tooltip"
            className={cn(
              "absolute left-[calc(100%+10px)] top-1/2 -translate-y-1/2 z-30",
              "whitespace-nowrap rounded-[9px] px-2.5 py-1.5",
              "text-[12.5px] font-medium leading-none",
              "bg-[var(--ink,hsl(var(--primary)))] text-[var(--surface,hsl(var(--primary-foreground)))]",
              "shadow-[0_6px_18px_rgba(10,37,64,0.18)]",
              "opacity-0 -translate-x-1.5 pointer-events-none",
              "transition-[transform,opacity] duration-[180ms] ease-[cubic-bezier(0.22,1,0.36,1)]",
              "group-hover/themetoggle:opacity-100 group-hover/themetoggle:translate-x-0",
              "group-focus-visible/themetoggle:opacity-100 group-focus-visible/themetoggle:translate-x-0",
              "motion-reduce:transition-none motion-reduce:translate-x-0",
            )}
          >
            {targetLabel}
          </span>
        ) : (
          <span>{targetLabel}</span>
        )}
      </button>
    );
  },
);
ThemeToggleButton.displayName = "ThemeToggleButton";
